"use client";

import {
  isStateAbbreviation,
  type StateAbbreviation,
} from "@nimble/datamodel/enums/states";
import { APIProvider } from "@vis.gl/react-google-maps";
import dynamic from "next/dynamic";
import * as React from "react";
import { useCallback, useState } from "react";

import LocationDropdown, {
  LocationDropdownProps,
} from "@/components/shared/location-dropdown/location-dropdown";
import useGooglePlacesSuggestions, {
  extractAddressComponent,
  JOB_SEARCH_COVERAGE,
} from "@/components/shared/location-dropdown/use-google-places-suggestions";
import { publicEnv } from "@/env/public-env";
import { neverthrowize } from "@/lib/data/call-server-api";
import {
  getAdvertisedStateConfigFromAbbreviation,
  IndividualStateConfig,
  isIndividualStateRoute,
  StateConfig,
} from "@/lib/states-config";

const LocationDropdownStateModal = dynamic(
  () =>
    import(
      "@/components/shared/location-dropdown/location-dropdown-state-check/dialogs/location-dropdown-state-modal"
    ),
  { ssr: false },
);

interface LocationDropdownStateCheckProps extends LocationDropdownProps {
  currentState: StateConfig;
  children?: React.ReactNode;
}

function getStateFromGeocode(
  geocodeResult: google.maps.GeocoderResult | null | undefined,
): StateAbbreviation | null {
  if (!geocodeResult?.address_components) {
    return null;
  }

  const value = extractAddressComponent(
    geocodeResult.address_components,
    ["administrative_area_level_1"],
    "short_name",
  );

  // Google can hand back a non-US region: parse it into the domain type here.
  return value && isStateAbbreviation(value) ? value : null;
}

function shouldShowDialog(
  currentState: StateConfig,
  addressStateConfig: IndividualStateConfig | null,
): boolean {
  if (!addressStateConfig) return true;

  if (isIndividualStateRoute(currentState.route)) {
    return currentState.abbreviation !== addressStateConfig.abbreviation;
  }
  return false;
}

function LocationDropdownStateCheckContent({
  value,
  onChange,
  currentState,
  ...props
}: LocationDropdownStateCheckProps) {
  const { fetchAddressDetails } = useGooglePlacesSuggestions({
    useStrictAddress: false,
    coverage: JOB_SEARCH_COVERAGE,
  });
  const [showDialog, setShowDialog] = useState(false);
  const [pendingValue, setPendingValue] = useState<string | null>(null);
  const [addressState, setAddressStateConfig] =
    useState<IndividualStateConfig | null>(null);

  const validateAndShowDialog = useCallback(
    (
      newValue: string,
      place: google.maps.GeocoderResult | null | undefined,
    ): void => {
      const stateAbbreviation = getStateFromGeocode(place);

      setPendingValue(newValue);
      const addressStateConfig =
        getAdvertisedStateConfigFromAbbreviation(stateAbbreviation);
      setAddressStateConfig(addressStateConfig);

      if (shouldShowDialog(currentState, addressStateConfig)) {
        setShowDialog(true);
        return;
      }

      onChange(newValue);
    },
    [currentState, onChange],
  );

  const handleChange = useCallback(
    async (newAddress: string) => {
      const details = neverthrowize([fetchAddressDetails(newAddress)]);
      await details.match(
        ([details]: [google.maps.GeocoderResult | null | undefined]) => {
          validateAndShowDialog(newAddress, details);
        },
        (_error) => {
          onChange(newAddress);
        },
      );
    },
    [validateAndShowDialog, fetchAddressDetails, onChange],
  );

  const handleOnCancel = useCallback(() => {
    setShowDialog(false);
    setPendingValue(null);
    setAddressStateConfig(null);
  }, []);

  return (
    <>
      <LocationDropdown value={value} onChange={handleChange} {...props} />
      {showDialog && pendingValue && (
        <LocationDropdownStateModal
          onCancel={handleOnCancel}
          selectedAddress={pendingValue}
          currentState={currentState}
          addressState={addressState}
        />
      )}
    </>
  );
}

export default function LocationDropdownStateCheck(
  props: LocationDropdownStateCheckProps,
) {
  return (
    <APIProvider apiKey={publicEnv.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || ""}>
      <LocationDropdownStateCheckContent {...props} />
    </APIProvider>
  );
}
